/**
 * Loans Operations Dashboard (M3X.LOANS §4.3 — HR Director command surface).
 *
 * The route /operations/loans previously redirected to the M16 list page.
 * Per Phase A, it's now a real Tashghil command surface: top-level
 * KPI tiles (active loans, pending approvals, Article 90 cap-firings,
 * arrears, refinance candidates) plus four action cards linking
 * deeper into the loans module.
 */

import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { ErrorPanel, Loading } from "../components/Loading";
import { PageHeader } from "../components/ui";
import { api } from "../lib/api";
import { useLegalEntityId } from "../lib/legalEntity";

type LoanReportSection = {
  name: string;
  headline: string;
  headline_ar: string;
  agent_attribution: string;
  data: Record<string, unknown>;
  metrics: Record<string, unknown>;
};

function _firstNumber(metrics: Record<string, unknown>): string {
  for (const v of Object.values(metrics)) {
    if (typeof v === "number" || typeof v === "string") {
      return String(v);
    }
  }
  return "—";
}

function KpiTile({
  label,
  value,
  agent,
}: {
  label: string;
  value: string;
  agent: string;
}) {
  return (
    <div className="rounded-lg border border-border-default bg-surface-default p-4">
      <div className="text-xs text-text-tertiary uppercase tracking-wide">
        {label}
      </div>
      <div className="mt-2 text-2xl font-semibold text-text-primary">
        {value}
      </div>
      <div className="mt-2 text-xs text-text-tertiary">{agent}</div>
    </div>
  );
}

function ActionCard({
  title,
  description,
  href,
}: {
  title: string;
  description: string;
  href: string;
}) {
  return (
    <a
      href={href}
      className="block rounded-lg border border-border-default bg-surface-default p-4 hover:border-brand-blue hover:shadow-sm"
    >
      <div className="font-medium text-text-primary">{title}</div>
      <div className="mt-1 text-xs text-text-secondary">{description}</div>
    </a>
  );
}

export function LoansOperationsDashboardPage() {
  const { t } = useTranslation();
  const legalEntityId = useLegalEntityId();

  const reportsQ = useQuery({
    queryKey: ["reports", "loans", legalEntityId, "dashboard"],
    queryFn: () =>
      api.getLoanReports(legalEntityId ?? undefined) as Promise<
        LoanReportSection[]
      >,
  });

  // Pick a fixed set of 5 sections for the KPI tiles (deterministic order
  // — sections that don't exist render as "—" tiles).
  const sectionByName = new Map(
    (reportsQ.data ?? []).map((s) => [s.name, s]),
  );

  const tiles: Array<{ label: string; key: string }> = [
    { label: t("loans_ops.kpi_outstanding"), key: "outstanding_per_le" },
    { label: t("loans_ops.kpi_cap_firings"), key: "cap_firing_frequency" },
    { label: t("loans_ops.kpi_default_rate"), key: "default_rate" },
    { label: t("loans_ops.kpi_avg_tenure"), key: "average_tenure" },
    { label: t("loans_ops.kpi_refinance"), key: "refinance_candidates" },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("loans_ops.title")}
        subtitle={t("loans_ops.subtitle")}
      />

      {reportsQ.isLoading && <Loading />}
      {reportsQ.error && (
        <ErrorPanel
          message={String((reportsQ.error as Error).message ?? reportsQ.error)}
        />
      )}

      <section>
        <h2 className="mb-3 text-sm font-medium text-text-primary uppercase tracking-wide">
          {t("loans_ops.headline_kpis")}
        </h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
          {tiles.map(({ label, key }) => {
            const sec = sectionByName.get(key);
            return (
              <KpiTile
                key={key}
                label={label}
                value={sec ? _firstNumber(sec.metrics) : "—"}
                agent={sec?.agent_attribution ?? ""}
              />
            );
          })}
        </div>
      </section>

      <section>
        <h2 className="mb-3 text-sm font-medium text-text-primary uppercase tracking-wide">
          {t("loans_ops.headline_actions")}
        </h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <ActionCard
            title={t("loans_ops.action_applications_title")}
            description={t("loans_ops.action_applications_desc")}
            href="/operations/loans/applications"
          />
          <ActionCard
            title={t("loans_ops.action_schedules_title")}
            description={t("loans_ops.action_schedules_desc")}
            href="/operations/loans/schedules"
          />
          <ActionCard
            title={t("loans_ops.action_reports_title")}
            description={t("loans_ops.action_reports_desc")}
            href="/reports/loans"
          />
          <ActionCard
            title={t("loans_ops.action_products_title")}
            description={t("loans_ops.action_products_desc")}
            href="/policies/loan-products"
          />
        </div>
      </section>
    </div>
  );
}

export default LoansOperationsDashboardPage;
